// TDS2 SHIM — replace with @salesforce/trailhead-ui UnitAbWelcomeStage + Button when TDS2 lands.
// Mirrors the prototype's NoOrgHero (unit-ab-state-plain--no-org) from
// UnitBuildAgent.tsx in the headless-agent-creation prototype: the state shown
// when a learner is logged in but has no connected playground org.
//
// Unlike the sibling presentational shims, this one is stateful — the primary
// CTA kicks off the real playground provisioning flow. To match the left-column
// SpecialityOrgManager button, clicking "Create Playground" first opens a
// confirmation modal (copy mirrors config/locales/views/speciality_org/en.yml
// `manage.modal`), and the CTA shows a spinner while the request is in flight.
// The lifecycle (POST /api/v1/orgs/provision_requests → poll the request →
// connect the playground) is ported from SpecialityOrgManager.jsx
// (createOrgRequest / createPoller), minus the redux/i18n layer. On success we
// reload so the server re-resolves the now-connected org and the panel renders
// the usable chat experience.
import React from 'react';
import PropTypes from 'prop-types';
import railsFetch from 'lib/railsFetch';
import Poller from 'lib/Poller';
import { store } from 'reducers';
import { initRequestOrg, showError } from 'actions/specialityOrgActions';
import DOMPurify from 'dompurify';
import BetaTag from 'components/agentforce/BetaTag';
import { t } from 'lib/I18n';

const PROVISION_URL = '/api/v1/orgs/provision_requests';
const POLLING_INTERVAL = 5000;
const POLLING_MAX_ATTEMPTS = 1200;
const ERROR_RETRY_COUNT = 1;

// Fallback only — the real copy comes from the `errorMessage` prop, which the
// server fills from the same locale key the left-column SpecialityOrgManager
// uses (speciality_org.manage.status.generic_error), so both controls match.
const GENERIC_ERROR =
  'We couldn’t create your playground. Please try again in a moment.';

// The SpecialityOrgManager status that means a playground is connected and the
// server will now resolve a user_org — i.e. our panel should reload into chat.
const CONNECTED_STATUS = 'CONNECTED';

// Reads the shared SpecialityOrgManager pending flag from the redux store.
// Defensive against a store shape that doesn't carry the slice (returns false).
function readStorePending() {
  if (typeof store.getState !== 'function') return false;
  const rootState = store.getState() || {};
  return !!(rootState.specialityOrg && rootState.specialityOrg.orgRequestPending);
}

// Reads the SpecialityOrgManager status (e.g. 'CONNECTED') from the redux store.
function readStoreStatus() {
  if (typeof store.getState !== 'function') return null;
  const rootState = store.getState() || {};
  return (rootState.specialityOrg && rootState.specialityOrg.status) || null;
}

function ExternalLinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export default class NoOrgState extends React.Component {
  static propTypes = {
    mascotUrl: PropTypes.string,
    unitUid: PropTypes.string,
    // Server-resolved playground request for this unit (when one exists but no
    // org is connected yet) — lets the panel persist a failed error across
    // reloads and resume polling a pending request, mirroring SpecialityOrgManager.
    orgRequestState: PropTypes.string,
    orgRequestStatusUrl: PropTypes.string,
    orgRequestAutoLogin: PropTypes.bool,
    // Localized failure copy (may contain a "Get Help" link). Sourced server-side
    // from the same locale key as SpecialityOrgManager so both controls match.
    errorMessage: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
    ctaLabel: PropTypes.string,
    helpUrl: PropTypes.string,
    helpLabel: PropTypes.string,
    modalHeading: PropTypes.string,
    modalBody: PropTypes.string,
    modalDescription: PropTypes.string,
    confirmLabel: PropTypes.string,
    dismissLabel: PropTypes.string,
  };

  static defaultProps = {
    title: 'This Badge Requires a New Custom Agentforce Playground',
    ctaLabel: 'Create Playground',
    helpUrl:
      'https://trailhead.salesforce.com/help?article=Connect-a-Salesforce-or-Social-Login-to-Your-Trailhead-Account',
    helpLabel: 'Stuck? Get Help',
    modalHeading: 'This Badge Requires a New Custom Headless 360 Playground.',
    modalBody: 'Your Playground only stays active for a limited time.',
    confirmLabel: 'Yes, Create Playground',
    dismissLabel: 'Not Yet',
  };

  // Seed from the server-resolved request state so a failed request persists its
  // error across reloads and a pending request shows the in-flight CTA.
  // `storePending` mirrors the shared redux `orgRequestPending` so a request
  // started from the left-column button also disables/spins our CTA.
  state = {
    pending: this.props.orgRequestState === 'pending',
    storePending: readStorePending(),
    error: this.props.orgRequestState === 'failed' ? this.errorCopy() : null,
    showConfirm: false,
  };

  poller = null;
  unsubscribeStore = null;

  // Prefer the server-provided (localized) copy; fall back to the bundled string.
  errorCopy() {
    return this.props.errorMessage || GENERIC_ERROR;
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);

    // Mirror the shared store so the left-column button and our CTA stay in sync
    // in BOTH directions — a request started over there reflects here too.
    if (typeof store.subscribe === 'function') {
      this.unsubscribeStore = store.subscribe(this.syncStorePending);
    }

    // Resume polling a request that was still in flight when the page loaded.
    if (this.props.orgRequestState === 'pending' && this.props.orgRequestStatusUrl) {
      this.markLeftColumnPending();
      this.startPoller(this.props.orgRequestStatusUrl);
    }
  }

  // The left-column playground control (SpecialityOrgManager) is redux-connected
  // and disables/spins off `state.specialityOrg.orgRequestPending`. We share the
  // same store, so dispatching these keeps that button in lockstep with our flow
  // — disabled while a request is in flight, error-cleared when ours fails.
  markLeftColumnPending() {
    store.dispatch(initRequestOrg());
  }

  markLeftColumnError() {
    store.dispatch(showError({}));
  }

  syncStorePending = () => {
    // A left-column request that connects updates the store to CONNECTED without
    // reloading (it's redux-driven). Our panel's no-org/chat decision is resolved
    // server-side, so reload to pick up the freshly connected org — matching what
    // our own CTA does on success.
    if (readStoreStatus() === CONNECTED_STATUS) {
      this.reload();
      return;
    }

    const storePending = readStorePending();
    if (storePending !== this.state.storePending) {
      this.setState({ storePending });
    }
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    if (this.poller) {
      this.poller.stop();
      this.poller = null;
    }
  }

  handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.state.showConfirm) {
      this.closeConfirm();
    }
  };

  // Terminal failure — stop polling, re-enable the CTA, surface an inline error,
  // and release the left-column button from its pending/spinner state.
  fail = () => {
    if (this.poller) {
      this.poller.stop();
      this.poller = null;
    }
    this.markLeftColumnError();
    this.setState({ pending: false, error: this.errorCopy() });
  };

  reload = () => {
    window.location.reload();
  };

  openConfirm = () => {
    // Don't offer to start a request when one is already running — ours or one
    // kicked off from the left-column button (mirrored via `storePending`).
    if (this.state.pending || this.state.storePending) return;
    this.setState({ showConfirm: true });
  };

  closeConfirm = () => {
    this.setState({ showConfirm: false });
  };

  handleConfirm = () => {
    this.setState({ showConfirm: false, pending: true, error: null });
    this.markLeftColumnPending();

    railsFetch({
      url: PROVISION_URL,
      method: 'post',
      data: { unit_uid: this.props.unitUid },
    })
      .then((response) => {
        const data = (response && response.data) || {};
        const statusUrl = PROVISION_URL + '/' + data.id;

        if (data.state === 'pending') {
          this.startPoller(statusUrl);
        } else if (data.state === 'created') {
          this.connectAndReload(statusUrl, data.auto_login);
        } else {
          this.fail();
        }
      })
      .catch((error) => {
        // A 409 means a request already exists (likely created in another tab).
        // Reload so the page picks up the existing request/org.
        if (error && error.railsFetchStatusCode === 409) {
          this.reload();
          return;
        }
        this.fail();
      });
  };

  startPoller(statusUrl) {
    // Captured retry counter — replicates SpecialityOrgManager's closure so
    // transient 5xx / network errors retry once before failing.
    let errorRetryCount = 0;

    if (this.poller) {
      this.poller.stop();
    }

    this.poller = new Poller({
      createRequest: () => railsFetch({ url: statusUrl, method: 'get' }),
      interval: POLLING_INTERVAL,
      maxRetries: POLLING_MAX_ATTEMPTS,
      isComplete: (response) => response.data.state !== 'pending',
      onComplete: (response) => {
        this.poller = null;
        if (response.data.state === 'created') {
          this.connectAndReload(statusUrl, response.data.auto_login);
        } else {
          this.fail();
        }
      },
      onError: (error, poller) => {
        if ([401, 403, 404].includes(error.railsFetchStatusCode)) {
          this.reload();
        } else if (
          (error.railsFetchStatusCode >= 500 || 'railsFetchError' in error) &&
          errorRetryCount < ERROR_RETRY_COUNT
        ) {
          errorRetryCount++;
          poller.retry();
        } else {
          this.fail();
        }
      },
      onTimeout: () => {
        this.fail();
      },
    });

    this.poller.start();
  }

  // Once the request is created, an auto-login playground still needs its
  // connection established before it shows up as the user's active org. Either
  // way, reload so the server re-resolves the connected org for this unit.
  connectAndReload(statusUrl, autoLogin) {
    if (!autoLogin) {
      this.reload();
      return;
    }

    railsFetch({ url: statusUrl + '/playground_connection', method: 'post' })
      .then(() => this.reload())
      .catch(() => this.fail());
  }

  renderConfirmModal() {
    const { modalHeading, modalBody, confirmLabel, dismissLabel } = this.props;
    const modalDescription = this.props.modalDescription || t('speciality_org.manage.modal.description');

    return (
      <div
        className="tds2-agentforce-confirm__backdrop"
        onClick={this.closeConfirm}
      >
        <div
          className="tds2-agentforce-confirm__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="no-org-confirm-heading"
          onClick={(event) => event.stopPropagation()}
        >
          <h3 className="tds2-agentforce-confirm__heading" id="no-org-confirm-heading">
            {modalHeading}
          </h3>
          <p className="tds2-agentforce-confirm__body">{modalBody}</p>
          <p
            className="tds2-agentforce-confirm__description"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modalDescription) }}
          />
          <div className="tds2-agentforce-confirm__actions">
            <button
              type="button"
              className="tds2-button tds2-button--secondary tds2-button--small"
              data-tds2-shim="Button"
              onClick={this.closeConfirm}
            >
              <span className="tds2-button__label">{dismissLabel}</span>
            </button>
            <button
              type="button"
              className="tds2-button tds2-button--primary tds2-button--small"
              data-tds2-shim="Button"
              onClick={this.handleConfirm}
            >
              <span className="tds2-button__label">{confirmLabel}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { mascotUrl, title, ctaLabel, helpUrl, helpLabel } = this.props;
    const body = this.props.body || t('speciality_org.manage.modal.body_default');
    const { pending, storePending, error, showConfirm } = this.state;
    // In-flight if OUR request is running or one was started from the left column.
    const inFlight = pending || storePending;

    return (
      <div
        className="tds2-agentforce-welcome"
        data-tds2-shim="UnitAbWelcomeStage"
      >
        <div
          className="tds2-agentforce-state-plain"
          role="status"
          aria-live="polite"
        >
          {mascotUrl ? (
            <img
              src={mascotUrl}
              alt=""
              width="120"
              className="tds2-agentforce-welcome__mascot"
              aria-hidden="true"
            />
          ) : null}
          <BetaTag />
          <h2 className="tds2-agentforce-state-plain__title">{title}</h2>
          <p className="tds2-agentforce-state-plain__body">{body}</p>
          <div className="tds2-agentforce-state-plain__actions">
            <button
              type="button"
              className="tds2-button tds2-button--primary tds2-button--small"
              data-tds2-shim="Button"
              onClick={this.openConfirm}
              disabled={inFlight}
            >
              {inFlight ? (
                <span className="tds2-button__spinner" aria-hidden="true" />
              ) : null}
              <span className="tds2-button__label">{ctaLabel}</span>
            </button>
            <a
              href={helpUrl}
              className="tds2-agentforce-state-plain__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {helpLabel}
              <ExternalLinkIcon />
            </a>
          </div>
          {error ? (
            <p
              className="tds2-agentforce-state-plain__error"
              role="alert"
              // Copy may carry a "Get Help" link (matches SpecialityOrgManager,
              // which also renders this string as sanitized HTML).
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(error, { ADD_ATTR: ['target'] }),
              }}
            />
          ) : null}
        </div>
        {showConfirm ? this.renderConfirmModal() : null}
      </div>
    );
  }
}
